/**
 * Cart line message editor for APO/MWS message properties.
 * Keeps the existing line-item properties intact and lets customers
 * update "Card Message" / "Personal Message" directly from cart.
 *
 * Targets the stable line item KEY (`id=<item.key>`) for /cart/change.js, NOT
 * the positional `line` index: Shopify prepends the newly-added delivery-fee
 * line at position 1, which shifts every other line's index, and the cart is
 * NOT re-rendered after the fee is added — so a render-time index goes stale and
 * the message would land on the (invisible) fee line. Because Shopify
 * recalculates a line's key on every property change, the key is refreshed from
 * each /cart/change.js response (`freshLineKeyFromCart`). Still saves via
 * form-encoded POST to avoid Shopify's JSON quantity-rule path that can emit
 * "increments of undefined" on SGF carts (form-encoding is orthogonal to the
 * id-vs-line targeting).
 *
 * Matches the WF (`shopify-theme`) + HF (`shopify-theme-hf`) fix. Covered by
 * `shopify-theme/tests/local-delivery/cart-line-messages.test.mjs`.
 */
(function () {
  'use strict';

  var SAVE_LABEL = 'Save message';
  var SKIP_LABEL = 'No, thanks';
  var BLANK_SAVE_PROMPT = 'No problem. If you\'d rather skip the card, tap "No, thanks".';
  var SKIP_SUCCESS_MESSAGE = 'No message needed for this arrangement.';
  var CHECKOUT_PROMPT_SINGLE = 'Before checkout, please either save your card message or tap "No, thanks" if you\'d rather leave it blank.';
  var CHECKOUT_PROMPT_MULTI = 'Before checkout, please either save a card message for each arrangement or tap "No, thanks" on the ones you\'d rather leave blank.';
  var INLINE_CHECKOUT_PROMPT = 'Please save your card message or tap "No, thanks" if you\'d like to skip it.';
  var DECLINED_STORAGE_KEY = 'wf-cart-line-message-skip:v1';

  function normalizeText(value) {
    return String(value == null ? '' : value).trim();
  }

  function decodeHtmlEntityOnce(value) {
    return String(value == null ? '' : value).replace(/&(#x?[0-9a-f]+|amp|lt|gt|quot|apos|nbsp);/gi, function (match, entity) {
      var name = String(entity || '').toLowerCase();
      if (name === 'amp') return '&';
      if (name === 'lt') return '<';
      if (name === 'gt') return '>';
      if (name === 'quot') return '"';
      if (name === 'apos') return "'";
      if (name === 'nbsp') return ' ';
      if (name.indexOf('#x') === 0) {
        var hex = parseInt(name.slice(2), 16);
        if (Number.isFinite(hex)) {
          try { return String.fromCodePoint(hex); } catch (_err) { return match; }
        }
      }
      if (name.indexOf('#') === 0) {
        var decimal = parseInt(name.slice(1), 10);
        if (Number.isFinite(decimal)) {
          try { return String.fromCodePoint(decimal); } catch (_err) { return match; }
        }
      }
      return match;
    });
  }

  function normalizeMessageText(value) {
    var current = normalizeText(value);
    for (var i = 0; i < 6; i++) {
      var next = normalizeText(decodeHtmlEntityOnce(current));
      if (next === current) break;
      current = next;
    }
    return current;
  }

  function getSessionStorage() {
    try {
      return window.sessionStorage || null;
    } catch (_err) {
      return null;
    }
  }

  function readDeclinedSelections() {
    var storage = getSessionStorage();
    if (!storage || typeof storage.getItem !== 'function') return {};
    try {
      var parsed = JSON.parse(storage.getItem(DECLINED_STORAGE_KEY) || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_err) {
      return {};
    }
  }

  function writeDeclinedSelections(next) {
    var storage = getSessionStorage();
    if (!storage || typeof storage.setItem !== 'function') return;
    try {
      storage.setItem(DECLINED_STORAGE_KEY, JSON.stringify(next && typeof next === 'object' ? next : {}));
    } catch (_err) {}
  }

  function getBlockIdentifier(block) {
    if (!block || typeof block.getAttribute !== 'function') return '';
    var key = normalizeText(block.getAttribute('data-line-key') || block.getAttribute('data-line-index'));
    var slot = normalizeText(block.getAttribute('data-line-message-slot'));
    return slot ? key + ':' + slot : key;
  }

  /**
   * BUG-025 slot helpers. Theme renders N editor blocks per cart line of quantity N
   * with property names "<base> <i> of <N>" (e.g. "Card Message 1 of 3"). The worker
   * (calendar-worker/src/lib/messageCard/messageCard.ts `extractPersonalMessagesPerSlot`)
   * uses the same regex shape to expand each slot into its own printed card.
   */
  function getBaseMessageName(block) {
    if (!block || typeof block.getAttribute !== 'function') return '';
    var explicit = normalizeText(block.getAttribute('data-line-message-base-name'));
    if (explicit) return explicit;
    return normalizeText(block.getAttribute('data-line-message-name'));
  }

  function buildSlotKey(baseName, slotIdx, totalSlots) {
    var base = normalizeText(baseName);
    if (!base) return '';
    if (!totalSlots || totalSlots <= 1) return base;
    return base + ' ' + slotIdx + ' of ' + totalSlots;
  }

  function resolveMessagePropertyName(block, fallbackName) {
    if (!block || typeof block.getAttribute !== 'function') return normalizeText(fallbackName);
    var existingName = normalizeText(block.getAttribute('data-line-message-name'));
    if (existingName) return existingName;

    var baseName = normalizeText(block.getAttribute('data-line-message-base-name')) || normalizeText(fallbackName);
    var slotIdx = Number(block.getAttribute('data-line-message-slot')) || 0;
    var totalSlots = Number(block.getAttribute('data-line-message-total-slots')) || 0;
    return buildSlotKey(baseName, slotIdx, totalSlots) || normalizeText(fallbackName);
  }

  function isSlotKeyForBase(propertyName, baseName) {
    var name = normalizeText(propertyName);
    var base = normalizeText(baseName);
    if (!name || !base) return false;
    if (name === base) return true;
    if (name.indexOf(base + ' ') !== 0) return false;
    var rest = name.substring(base.length + 1);
    return /^\d+\s+of\s+\d+$/i.test(rest);
  }

  function extractSlotIndex(propertyName, baseName) {
    var name = normalizeText(propertyName);
    var base = normalizeText(baseName);
    if (!name || !base || name.indexOf(base + ' ') !== 0) return 0;
    var match = name.substring(base.length + 1).match(/^(\d+)\s+of\s+(\d+)$/i);
    if (!match) return 0;
    return parseInt(match[1], 10) || 0;
  }

  function getStatusEl(block) {
    return block && typeof block.querySelector === 'function'
      ? block.querySelector('[data-wf-line-message-status]')
      : null;
  }

  function getInput(block) {
    return block && typeof block.querySelector === 'function'
      ? block.querySelector('[data-wf-line-message-input]')
      : null;
  }

  function getPropsEl(block) {
    return block && typeof block.querySelector === 'function'
      ? block.querySelector('script[data-wf-line-message-properties]')
      : null;
  }

  function getSkipBtn(block) {
    return block && typeof block.querySelector === 'function'
      ? block.querySelector('[data-wf-line-message-skip]')
      : null;
  }

  function getSaveBtn(block) {
    return block && typeof block.querySelector === 'function'
      ? block.querySelector('[data-wf-line-message-save]')
      : null;
  }

  function setStatus(statusEl, message, isError) {
    if (!statusEl) return;
    statusEl.textContent = message || '';
    statusEl.style.color = isError ? '#b42318' : '';
  }

  function nudgeToCheckout() {
    try {
      var cta = document.querySelector('[name="checkout"]');
      if (!cta || typeof cta.getBoundingClientRect !== 'function') return;
      var r = cta.getBoundingClientRect();
      // Only nudge if the checkout button is still below the viewport, so the
      // shopper sees there is more to do (complete the purchase) below.
      if (r.top > window.innerHeight - 60) {
        window.scrollBy({ top: 180, left: 0, behavior: 'smooth' });
      }
    } catch (e) {}
  }

  function flashSuccess(block, message) {
    var statusEl = getStatusEl(block);
    if (!statusEl) return;
    var isSkip = message === SKIP_SUCCESS_MESSAGE;
    // Persistent saved/skipped state: green Save button + SingBee confirmation +
    // scroll-to-checkout cue (all CSS-driven off these classes). Reverted on edit.
    if (block && block.classList) {
      block.classList.toggle('wf-msg-skipped', isSkip);
      block.classList.toggle('wf-msg-saved', !isSkip);
      nudgeToCheckout();
    }
    if (isSkip) {
      // Skip has no confirmation box, so keep the brief inline note.
      setStatus(statusEl, message, false);
      statusEl.style.color = '#2e7d32';
      setTimeout(function () { setStatus(statusEl, '', false); }, 2500);
    } else {
      // Save: the "Card message saved!" box is the confirmation, so no redundant "Saved" text.
      setStatus(statusEl, '', false);
    }
  }

  function clearBlockStatus(block) {
    setStatus(getStatusEl(block), '', false);
  }

  function setDeclinedState(block, next) {
    if (!block || typeof block.setAttribute !== 'function') return;
    var normalizedNext = !!next;
    if (normalizedNext) {
      block.setAttribute('data-line-message-declined', 'true');
    } else if (typeof block.removeAttribute === 'function') {
      block.removeAttribute('data-line-message-declined');
    }

    var skipBtn = getSkipBtn(block);
    if (skipBtn) {
      if (typeof skipBtn.setAttribute === 'function') {
        skipBtn.setAttribute('aria-pressed', normalizedNext ? 'true' : 'false');
      }
      if (normalizedNext) {
        if (typeof skipBtn.setAttribute === 'function') skipBtn.setAttribute('data-selected', 'true');
      } else if (typeof skipBtn.removeAttribute === 'function') {
        skipBtn.removeAttribute('data-selected');
      }
    }

    var blockId = getBlockIdentifier(block);
    if (!blockId) return;
    var declinedSelections = readDeclinedSelections();
    if (normalizedNext) declinedSelections[blockId] = true;
    else delete declinedSelections[blockId];
    writeDeclinedSelections(declinedSelections);
  }

  function isDeclined(block) {
    if (!block || typeof block.getAttribute !== 'function') return false;
    if (normalizeText(block.getAttribute('data-line-message-declined')).toLowerCase() === 'true') return true;
    var blockId = getBlockIdentifier(block);
    if (!blockId) return false;
    return !!readDeclinedSelections()[blockId];
  }

  function isEligibleBlock(block) {
    if (!block || typeof block.getAttribute !== 'function') return false;
    if (normalizeText(block.getAttribute('data-line-message-excluded')).toLowerCase() === 'true') return false;
    return !(block.style && block.style.display === 'none');
  }

  /**
   * Shopify outputs {{ item.properties | json }} as either:
   *   - object: {"key":"val",...}
   *   - array of pairs: [["key","val"],["key2","val2"],...]
   * We must handle both formats.
   */
  function parseProperties(raw) {
    if (!raw) return {};
    try {
      var parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        var obj = {};
        for (var i = 0; i < parsed.length; i++) {
          var pair = parsed[i];
          if (Array.isArray(pair) && pair.length >= 2) {
            obj[String(pair[0])] = pair[1];
          }
        }
        return obj;
      }
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_err) {
      return {};
    }
  }

  /**
   * BUG-025: when `options.baseName` and `options.totalSlots` are provided, also prune
   * orphan slot keys. Orphans appear when:
   *   - quantity decreases (e.g. 3 → 2 leaves a stale "Card Message 3 of 3")
   *   - quantity drops to 1 (any "X of N" key becomes orphaned; collapse to single base)
   *   - a stale legacy single key lingers next to fresh slot keys (e.g. base alone present
   *     when slot keys are now active)
   * Without this prune the customer-facing cart and the worker's PDF would carry stale text.
   */
  function buildUpdatedProperties(existingProperties, propertyName, nextValue, options) {
    var props = {};
    var existing = existingProperties && typeof existingProperties === 'object' ? existingProperties : {};
    Object.keys(existing).forEach(function (key) {
      var val = existing[key];
      props[key] = val == null ? '' : String(val);
    });

    var targetName = normalizeText(propertyName);
    if (!targetName) return props;

    var value = normalizeMessageText(nextValue);
    if (value) props[targetName] = value;
    else delete props[targetName];

    var opts = options && typeof options === 'object' ? options : {};
    var baseName = normalizeText(opts.baseName);
    var totalSlots = Number(opts.totalSlots) || 0;
    if (baseName && totalSlots > 0) {
      Object.keys(props).forEach(function (key) {
        if (!isSlotKeyForBase(key, baseName)) return;
        var slot = extractSlotIndex(key, baseName);
        if (totalSlots === 1) {
          if (key !== baseName) delete props[key];
          return;
        }
        if (slot === 0 && key === baseName) {
          delete props[key];
          return;
        }
        if (slot > totalSlots) delete props[key];
      });
    }

    return props;
  }

  function getRawStoredMessageValue(block) {
    var propsEl = getPropsEl(block);
    var propertyName = normalizeText(block && typeof block.getAttribute === 'function'
      ? block.getAttribute('data-line-message-name')
      : '');
    if (!propsEl || !propertyName) return '';
    var existing = parseProperties(propsEl.textContent || '');
    return normalizeText(existing[propertyName]);
  }

  function getStoredMessageValue(block) {
    return normalizeMessageText(getRawStoredMessageValue(block));
  }

  function syncDeclinedState(block) {
    if (!block) return;
    var input = getInput(block);
    var currentValue = normalizeMessageText(input && typeof input.value === 'string' ? input.value : '');
    var storedValue = getStoredMessageValue(block);
    if (currentValue || storedValue) {
      setDeclinedState(block, false);
      return;
    }
    setDeclinedState(block, isDeclined(block));
  }

  /**
   * Form-encoded POST avoids Shopify's JSON quantity_rule validation path,
   * which can emit bogus "increments of undefined" errors on SGF carts.
   */
  function xhrPostFormEncoded(url, lineKey, quantity, properties) {
    return new Promise(function (resolve, reject) {
      // Target the stable line item KEY (id=), not the positional line index:
      // Shopify prepends the newly-added delivery-fee line at position 1, which
      // shifts every other line's index, so an index-keyed write misattributes
      // the message to the (invisible) fee line. Form-encoding is kept to avoid
      // the JSON quantity_rule "increments of undefined" path.
      var parts = ['id=' + encodeURIComponent(lineKey), 'quantity=' + quantity];
      if (properties && typeof properties === 'object') {
        Object.keys(properties).forEach(function (key) {
          parts.push('properties[' + encodeURIComponent(key) + ']=' + encodeURIComponent(properties[key]));
        });
      }
      var xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (!xhr.responseText) {
            resolve({});
            return;
          }
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (_err) {
            resolve({ raw: xhr.responseText });
          }
        } else {
          reject(new Error(xhr.responseText || ('Cart change failed: ' + xhr.status)));
        }
      };
      xhr.onerror = function () { reject(new Error('Network error')); };
      xhr.send(parts.join('&'));
    });
  }

  function setButtonLoadingState(block, next, mode) {
    var saveBtn = getSaveBtn(block);
    var skipBtn = getSkipBtn(block);
    if (saveBtn) {
      saveBtn.disabled = !!next;
      saveBtn.textContent = next && mode !== 'decline' ? 'Saving...' : SAVE_LABEL;
    }
    if (skipBtn) {
      skipBtn.disabled = !!next;
      skipBtn.textContent = next && mode === 'decline' ? 'Saving...' : SKIP_LABEL;
    }
  }

  // Stable line-item key helpers (mirror WF/HF). The clean item.key lives on the
  // data-wf-line-message-group wrapper; refresh it from each cart/change.js
  // response because Shopify recalculates a line's key on every property change.
  function getLineItemKey(block) {
    if (!block || typeof block.getAttribute !== 'function') return '';
    var group = typeof block.closest === 'function' ? block.closest('[data-wf-line-message-group]') : null;
    if (group && typeof group.getAttribute === 'function') {
      var groupKey = normalizeText(group.getAttribute('data-line-key'));
      if (groupKey) return groupKey;
    }
    return normalizeText(block.getAttribute('data-line-key')).replace(/:\d+$/, '');
  }

  function freshLineKeyFromCart(cart, writtenProperties) {
    if (!cart || !Array.isArray(cart.items)) return '';
    var keys = Object.keys(writtenProperties || {});
    if (!keys.length) return '';
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var props = item && item.properties;
      if (!props || typeof props !== 'object') continue;
      var match = true;
      for (var j = 0; j < keys.length; j++) {
        if (normalizeText(props[keys[j]]) !== normalizeText(writtenProperties[keys[j]])) { match = false; break; }
      }
      if (match && item.key) return item.key;
    }
    return '';
  }

  function setLineItemKey(block, freshKey) {
    if (!block || !freshKey) return;
    var group = typeof block.closest === 'function' ? block.closest('[data-wf-line-message-group]') : null;
    if (group && typeof group.setAttribute === 'function') group.setAttribute('data-line-key', freshKey);
  }

  function saveLineMessage(block, options) {
    var opts = options || {};
    if (!block || block.getAttribute('data-saving') === 'true') return Promise.resolve(false);

    var input = getInput(block);
    var statusEl = getStatusEl(block);
    var propsEl = getPropsEl(block);
    var lineKey = getLineItemKey(block);
    var lineQuantity = Number(block.getAttribute('data-line-quantity')) || 1;
    var propertyName = normalizeText(block.getAttribute('data-line-message-name'));
    var mode = opts.mode === 'decline' ? 'decline' : 'save';

    if (!input || !propsEl || !lineKey || !propertyName) {
      if (!opts.silent) setStatus(statusEl, 'We could not update your message. Please refresh and try again.', true);
      return Promise.resolve(false);
    }

    var nextValue = mode === 'decline' ? '' : input.value;
    if (mode !== 'decline' && !normalizeText(nextValue)) {
      setDeclinedState(block, false);
      if (!opts.silent) setStatus(statusEl, BLANK_SAVE_PROMPT, true);
      return Promise.resolve(false);
    }

    var existingParsed = parseProperties(propsEl.textContent || '');
    var baseName = getBaseMessageName(block);
    var totalSlots = Number(block.getAttribute('data-line-message-total-slots')) || 0;
    var nextProperties = buildUpdatedProperties(existingParsed, propertyName, nextValue, {
      baseName: baseName,
      totalSlots: totalSlots || lineQuantity,
    });
    block.setAttribute('data-saving', 'true');
    if (!opts.silent) setButtonLoadingState(block, true, mode);

    function onSaveSuccess() {
      propsEl.textContent = JSON.stringify(nextProperties);
      if (mode === 'decline') {
        input.value = '';
        setDeclinedState(block, true);
        if (!opts.silent) flashSuccess(block, SKIP_SUCCESS_MESSAGE);
      } else {
        input.value = normalizeMessageText(input.value);
        setDeclinedState(block, false);
        if (!opts.silent) flashSuccess(block, 'Saved');
      }
      return true;
    }

    return xhrPostFormEncoded('/cart/change.js', lineKey, lineQuantity, nextProperties)
      .then(function (response) {
        // Refresh the cached key: this write changed the line's properties, so
        // Shopify recalculated its key and the next save must target the new one.
        var freshKey = freshLineKeyFromCart(response, nextProperties);
        if (freshKey) setLineItemKey(block, freshKey);
        return onSaveSuccess();
      })
      .catch(function (err) {
        console.error('[wf-cart-line-messages] save failed', err);
        if (!opts.silent) setStatus(statusEl, 'We could not save your message. Please try again.', true);
        return false;
      })
      .finally(function () {
        block.removeAttribute('data-saving');
        if (!opts.silent) setButtonLoadingState(block, false, mode);
      });
  }

  function declineLineMessage(block, options) {
    var opts = options || {};
    if (!block) return Promise.resolve(false);

    var input = getInput(block);
    var propsEl = getPropsEl(block);
    var propertyName = normalizeText(block.getAttribute('data-line-message-name'));
    if (!input || !propsEl || !propertyName) {
      if (!opts.silent) setStatus(getStatusEl(block), 'We could not update your message. Please refresh and try again.', true);
      return Promise.resolve(false);
    }

    var storedValue = getStoredMessageValue(block);
    input.value = '';
    clearBlockStatus(block);

    if (!storedValue) {
      propsEl.textContent = JSON.stringify(buildUpdatedProperties(parseProperties(propsEl.textContent || ''), propertyName, ''));
      setDeclinedState(block, true);
      if (!opts.silent) flashSuccess(block, SKIP_SUCCESS_MESSAGE);
      return Promise.resolve(true);
    }

    return saveLineMessage(block, {
      mode: 'decline',
      silent: !!opts.silent,
    });
  }

  /**
   * Saves all message editors that have unsaved changes.
   * Skips page reload - intended for pre-checkout auto-save.
   * Returns a Promise that resolves with the save results.
   */
  function saveAllUnsavedMessages() {
    var blocks = document.querySelectorAll('[data-wf-line-message-block]');
    var saves = [];

    blocks.forEach(function (block) {
      if (!isEligibleBlock(block)) return;

      var input = getInput(block);
      var propsEl = getPropsEl(block);
      var propertyName = normalizeText(block.getAttribute('data-line-message-name'));
      if (!input || !propsEl || !propertyName) return;

      var currentValue = normalizeMessageText(input.value);
      var existing = parseProperties(propsEl.textContent || '');
      var storedRaw = normalizeText(existing[propertyName]);
      var storedValue = normalizeMessageText(storedRaw);

      if (!currentValue) return;
      if (currentValue === storedValue && storedRaw === currentValue) return;

      saves.push(saveLineMessage(block, { skipReload: true, silent: true }));
    });

    if (saves.length === 0) return Promise.resolve([]);
    return Promise.all(saves);
  }

  function getCheckoutBlockingState() {
    var blocks = document.querySelectorAll('[data-wf-line-message-block]');
    var blockingBlocks = [];

    blocks.forEach(function (block) {
      if (!isEligibleBlock(block)) return;
      var input = getInput(block);
      var propsEl = getPropsEl(block);
      var propertyName = normalizeText(block.getAttribute('data-line-message-name'));
      if (!input || !propsEl || !propertyName) return;

      if (normalizeMessageText(input.value)) return;
      if (isDeclined(block)) return;
      blockingBlocks.push(block);
    });

    if (blockingBlocks.length === 0) {
      return { ok: true, blockingBlocks: [], message: '' };
    }

    return {
      ok: false,
      blockingBlocks: blockingBlocks,
      message: blockingBlocks.length > 1 ? CHECKOUT_PROMPT_MULTI : CHECKOUT_PROMPT_SINGLE,
    };
  }

  function promptCheckoutBlockers(state) {
    if (!state || state.ok || !state.blockingBlocks || !state.blockingBlocks.length) return;
    var firstBlock = state.blockingBlocks[0];
    setStatus(getStatusEl(firstBlock), INLINE_CHECKOUT_PROMPT, true);
    var input = getInput(firstBlock);
    if (input && typeof input.focus === 'function') input.focus();
    if (firstBlock && typeof firstBlock.scrollIntoView === 'function') {
      try {
        firstBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_err) {
        firstBlock.scrollIntoView();
      }
    }
  }

  function prepareMessagesForCheckout() {
    var blockingState = getCheckoutBlockingState();
    if (!blockingState.ok) {
      promptCheckoutBlockers(blockingState);
      return Promise.resolve(blockingState);
    }

    return saveAllUnsavedMessages().then(function (results) {
      var saveResults = Array.isArray(results) ? results : [];
      var hasFailure = saveResults.some(function (result) { return result === false; });
      if (hasFailure) {
        return {
          ok: false,
          blockingBlocks: [],
          message: 'We could not save one of your messages. Please try again.',
        };
      }
      return { ok: true, blockingBlocks: [], message: '' };
    });
  }

  function onDocumentClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var skipBtn = target.closest('[data-wf-line-message-skip]');
    if (skipBtn) {
      event.preventDefault();
      event.stopPropagation();
      declineLineMessage(skipBtn.closest('[data-wf-line-message-block]'));
      return;
    }

    var saveBtn = target.closest('[data-wf-line-message-save]');
    if (!saveBtn) return;
    event.preventDefault();
    event.stopPropagation();
    saveLineMessage(saveBtn.closest('[data-wf-line-message-block]'));
  }

  function onDocumentInput(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var input = target.closest('[data-wf-line-message-input]');
    if (!input) return;
    var block = input.closest('[data-wf-line-message-block]');
    if (!block) return;
    // Editing again clears the saved/skipped confirmation so they can re-save.
    if (block.classList) block.classList.remove('wf-msg-saved', 'wf-msg-skipped');
    if (normalizeMessageText(input.value)) {
      setDeclinedState(block, false);
    }
    clearBlockStatus(block);
  }

  function onDocumentKeydown(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (event.key !== 'Enter' || (!event.metaKey && !event.ctrlKey)) return;
    var input = target.closest('[data-wf-line-message-input]');
    if (!input) return;
    event.preventDefault();
    saveLineMessage(input.closest('[data-wf-line-message-block]'));
  }

  document.addEventListener('click', onDocumentClick);
  document.addEventListener('input', onDocumentInput);
  document.addEventListener('keydown', onDocumentKeydown);

  var _lastMessageCardConfig = null;
  var _reInitTimer = null;
  var _pubSubBound = false;
  var _observerBound = false;

  function initMessageCards(messageCardConfig) {
    var config = messageCardConfig && typeof messageCardConfig === 'object' ? messageCardConfig : {};
    _lastMessageCardConfig = config;
    if (!config.enabled) return;
    var propertyName = normalizeText(config.propertyName);
    if (!propertyName) return;
    var excludedIds = Array.isArray(config.excludedProductIds)
      ? config.excludedProductIds.map(String)
      : [];
    var excludedSet = {};
    excludedIds.forEach(function (id) { excludedSet[id] = true; });

    var pending = document.querySelectorAll('[data-wf-line-message-pending]');
    pending.forEach(function (block) {
      var productId = block.getAttribute('data-product-id') || '';
      if (excludedSet[productId]) {
        block.setAttribute('data-line-message-excluded', 'true');
        block.removeAttribute('data-wf-line-message-pending');
        block.style.display = 'none';
        return;
      }
      var input = getInput(block);
      var hasExisting = input && normalizeText(input.value);
      if (!hasExisting) {
        var existingPropertyName = normalizeText(block.getAttribute('data-line-message-name'));
        var nextPropertyName = resolveMessagePropertyName(block, propertyName);
        block.setAttribute('data-line-message-name', nextPropertyName);
        if (!normalizeText(block.getAttribute('data-line-message-base-name'))) {
          block.setAttribute('data-line-message-base-name', propertyName);
        }
        if (!existingPropertyName) {
          var label = block.querySelector('.wf-line-message__label');
          if (label) label.textContent = propertyName;
        }
      }
      block.removeAttribute('data-line-message-excluded');
      block.removeAttribute('data-wf-line-message-pending');
      block.style.display = '';
      if (input && typeof input.value === 'string') {
        var normalizedInput = normalizeMessageText(input.value);
        if (input.value !== normalizedInput) input.value = normalizedInput;
      }
      syncDeclinedState(block);
    });

    observeCartSectionReRenders();
  }

  function hasPendingBlocks() {
    return document.querySelectorAll('[data-wf-line-message-pending]').length > 0;
  }

  function scheduleReInit() {
    if (_reInitTimer) clearTimeout(_reInitTimer);
    _reInitTimer = setTimeout(function () {
      _reInitTimer = null;
      if (_lastMessageCardConfig && hasPendingBlocks()) {
        initMessageCards(_lastMessageCardConfig);
      }
    }, 120);
  }

  /**
   * Dawn's cart-items re-renders .js-contents on cartUpdate events
   * (triggered when fee products are added/removed). After the re-render,
   * wf-line-message blocks can come back with
   * data-wf-line-message-pending (and older templates may also hide them).
   * Two mechanisms detect and recover:
   *
   * 1. PubSub: subscribe to Dawn's cartUpdate event, then poll for
   *    pending blocks with retry (the DOM swap is async after the event).
   * 2. MutationObserver: watch #main-cart-items (parent) so the observer
   *    survives even if .js-contents itself is replaced.
   */
  function observeCartSectionReRenders() {
    if (typeof document === 'undefined') return;

    if (!_pubSubBound) {
      try {
        if (typeof subscribe === 'function' &&
            typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS &&
            PUB_SUB_EVENTS.cartUpdate) {
          _pubSubBound = true;
          subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
            retryInitPendingBlocks([200, 600, 1500]);
          });
        }
      } catch (_) {}
    }

    if (!_observerBound && typeof MutationObserver !== 'undefined') {
      var container = document.getElementById('main-cart-items');
      if (container) {
        _observerBound = true;
        var observer = new MutationObserver(function () {
          if (hasPendingBlocks()) scheduleReInit();
        });
        observer.observe(container, { childList: true, subtree: true });
      }
    }
  }

  function retryInitPendingBlocks(delays) {
    if (!delays || delays.length === 0 || !_lastMessageCardConfig) return;
    setTimeout(function () {
      if (hasPendingBlocks()) {
        initMessageCards(_lastMessageCardConfig);
      } else if (delays.length > 1) {
        retryInitPendingBlocks(delays.slice(1));
      }
    }, delays[0]);
  }

  window.WF_CART_LINE_MESSAGES = {
    parseProperties: parseProperties,
    normalizeMessageText: normalizeMessageText,
    buildUpdatedProperties: buildUpdatedProperties,
    saveLineMessage: saveLineMessage,
    declineLineMessage: declineLineMessage,
    saveAllUnsavedMessages: saveAllUnsavedMessages,
    getCheckoutBlockingState: getCheckoutBlockingState,
    prepareMessagesForCheckout: prepareMessagesForCheckout,
    initMessageCards: initMessageCards,
  };
})();
